import { Close } from '@wfp/icons-react';
import { DatePicker, Tag } from '@wfp/react';
import format from 'date-fns/format';
import React, { useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import ConfirmationModal from 'src/components/ConfirmationModal';
import TableMain from 'src/components/TableMain';
import { CALENDAR_FULL_DATE_FORMAT, STATUS_COLORS } from 'src/constants';
import OperationIdCell from '../OperationIdCell';

const TRANSITIONS_ORDER = [
  'new',
  'initial_draft',
  'review',
  'cd_approval',
  'rd_approval',
  'rd_approved',
  'published',
];

const STATUS_TITLES = {
  New: 'new',
  'Initial Draft': 'initial_draft',
  Review: 'review',
  'CD Approval': 'cd_approval',
  'RD Approval': 'rd_approval',
  'RD Approved': 'rd_approved',
  Published: 'published',
};

const dataPickerConfig = {
  dateFormat: CALENDAR_FULL_DATE_FORMAT,
  monthsShown: 2,
  placeholderText: 'Date',
  popperPlacement: 'bottom-start',
};

const isStatusLate = (currentStatus, statusToCheck, dueDate) => {
  const now = Date.now();
  const checkedStatusOrder = TRANSITIONS_ORDER.indexOf(statusToCheck);
  const currentStatusOrder = TRANSITIONS_ORDER.indexOf(
    STATUS_TITLES[currentStatus],
  );

  if (currentStatusOrder === -1 || checkedStatusOrder === -1) {
    console.error('unknown status', currentStatusOrder, checkedStatusOrder);
    return false;
  }
  return currentStatusOrder <= checkedStatusOrder && now >= new Date(dueDate);
};

const ProjectStatusTab = ({
  data,
  loading,
  filterText,
  filterSelect,
  handleDateChange,
  handleDateReset,
  period,
}) => {
  const [dateReset, setDateReset] = useState(null);

  const periodFrom2025 = parseInt(period) >= 2025;

  function getReadableStatus(status) {
    return Object.keys(STATUS_TITLES).find(
      key => STATUS_TITLES[key] === status,
    );
  }

  function getDateObj(date) {
    return date ? new Date(date) : null;
  }

  function getDateClassname(date, status, statusToCheck) {
    let className = 'due-date ';
    if (date && status) {
      className += isStatusLate(status, statusToCheck, date)
        ? 'late'
        : 'in-time';
    }

    return className;
  }

  const ResetDateButton = ({ date, status, pk, projectName }) => {
    if (!date) return <></>;

    return (
      <div
        onClick={() =>
          setDateReset({
            status,
            pk,
            date,
            projectName,
          })
        }
        className="reset-date-btn"
      >
        <Close color="#fff" />
      </div>
    );
  };

  const columns = [
    {
      Header: 'Operation ID',
      accessor: 'project_code',
      id: 'project_code_column',
      Filter: column => filterText(column),
      Cell: ({ row }) => <OperationIdCell row={row} />,
    },
    {
      Header: 'Regional Office',
      accessor: 'project_regional_bureau_display_code',
      id: 'rb_column',
      Filter: column =>
        filterSelect('project_regional_bureau_display_code', column),
    },
    {
      Header: 'CRF',
      accessor: 'crf',
      id: 'crf',
      Cell: ({ value }) => (value ? value : '-'),
      Filter: column => filterSelect('crf', column),
    },
    {
      Header: 'Country',
      accessor: 'project_country',
      id: 'project_country_column',
      filter: 'equals',
      Filter: column => filterSelect('project_country', column, true),
    },
    {
      Header: 'Current Status',
      accessor: 'current_status',
      id: 'current_status_column',
      className: 'current-status-column',
      Filter: column => filterSelect('current_status', column),
      Cell: ({ value }) => {
        const colors = STATUS_COLORS[value] || {
          background: '#E0E0E0',
          color: '#333333',
        };
        return (
          <Tag
            style={{
              backgroundColor: colors.background,
              color: colors.color,
            }}
          >
            {value}
          </Tag>
        );
      },
    },
    {
      Header: 'Initial Draft',
      accessor: 'initial_draft',
      id: 'initial_draft_column',
      className: 'date-picker-column',
      Cell: ({ row }) => {
        const date = getDateObj(row.original?.initial_draft);
        const className = getDateClassname(
          date,
          row.original.current_status,
          'initial_draft',
        );
        return (
          <div className={className}>
            <DatePicker
              datePicker={ReactDatePicker}
              startDate={date}
              setStartDate={newDate =>
                handleDateChange(newDate, 'initial_draft', row.original.pk)
              }
              hideCalendarIcon
              datePickerProps={{
                disabled: loading,
                className,
                ...dataPickerConfig,
              }}
            />
            <ResetDateButton
              date={date}
              status="initial_draft"
              pk={row.original.pk}
              projectName={row.original.project_code}
            />
          </div>
        );
      },
      Filter: column => filterSelect('initial_draft', column),
    },
    {
      Header: 'Review',
      accessor: 'review',
      id: 'review_column',
      className: 'date-picker-column',
      Cell: ({ row }) => {
        const date = getDateObj(row.original?.review);
        const className = getDateClassname(
          date,
          row.original.current_status,
          'review',
        );
        return (
          <div className={className}>
            <DatePicker
              datePicker={ReactDatePicker}
              startDate={date}
              setStartDate={newDate =>
                handleDateChange(newDate, 'review', row.original.pk)
              }
              hideCalendarIcon
              datePickerProps={{
                disabled: loading,
                className,
                ...dataPickerConfig,
              }}
            />
            <ResetDateButton
              date={date}
              status="review"
              pk={row.original.pk}
              projectName={row.original.project_code}
            />
          </div>
        );
      },
      Filter: column => filterSelect('review', column),
    },
    {
      Header: 'CD Approval',
      accessor: 'cd_approval',
      id: 'cd_approval_column',
      className: 'date-picker-column',
      Cell: ({ row }) => {
        const date = getDateObj(row.original?.cd_approval);
        const className = getDateClassname(
          date,
          row.original.current_status,
          'cd_approval',
        );
        return (
          <div className={className}>
            <DatePicker
              datePicker={ReactDatePicker}
              startDate={date}
              setStartDate={newDate =>
                handleDateChange(newDate, 'cd_approval', row.original.pk)
              }
              hideCalendarIcon
              datePickerProps={{
                disabled: loading,
                className,
                ...dataPickerConfig,
              }}
            />
            <ResetDateButton
              date={date}
              status="cd_approval"
              pk={row.original.pk}
              projectName={row.original.project_code}
            />
          </div>
        );
      },
      Filter: column => filterSelect('cd_approval', column),
    },
    {
      Header: 'RD Approval',
      accessor: 'rd_approval',
      id: 'rd_approval_column',
      className: 'date-picker-column',
      Cell: ({ row }) => {
        const date = getDateObj(row.original?.rd_approval);
        const className = getDateClassname(
          date,
          row.original.current_status,
          'rd_approval',
        );

        return (
          <div className={className}>
            <DatePicker
              datePicker={ReactDatePicker}
              startDate={date}
              setStartDate={newDate =>
                handleDateChange(newDate, 'rd_approval', row.original.pk)
              }
              hideCalendarIcon
              datePickerProps={{
                disabled: loading,
                className,
                ...dataPickerConfig,
              }}
            />
            <ResetDateButton
              date={date}
              status="rd_approval"
              pk={row.original.pk}
              projectName={row.original.project_code}
            />
          </div>
        );
      },
      Filter: column => filterSelect('rd_approval', column),
    },
  ];

  return (
    <>
      <TableMain
        data={data}
        columns={columns}
        pagination="client"
        className="-striped -highlight"
        loading={loading}
        isFilterable
        initialState={{
          sortBy: [
            {
              id: 'project_country_column',
              desc: false,
            },
          ],
          hiddenColumns: periodFrom2025 ? ['rd_approval_column'] : [],
        }}
      />
      {dateReset !== null && (
        <ConfirmationModal
          open={true}
          onSubmit={() => {
            handleDateReset(dateReset.status, dateReset.pk);
            setDateReset(null);
          }}
          onClose={() => setDateReset(null)}
          heading={`Clear '${getReadableStatus(dateReset.status)}' date for ${
            dateReset.projectName
          } ?`}
          content={
            <>
              <div className="revert-version-subtitle">
                {`Do you really want to clear date ${format(
                  dateReset.date,
                  CALENDAR_FULL_DATE_FORMAT,
                )} ?`}
              </div>
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
        />
      )}
    </>
  );
};

export default ProjectStatusTab;
